import React from 'react';
import { Book, Wallet, Code, Shield, Zap, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Documentation = () => {
  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
            <Book className="text-brand-primary" size={36} />
          </div>
          <h1 className="display-large mb-4" style={{ color: 'var(--text-primary)' }}>Platform Documentation</h1>
          <p className="body-medium" style={{ color: 'var(--text-muted)' }}>Complete guide to using MatrixLaunch</p>
        </div>

        {/* Quick Start Guide */}
        <section className="mb-16">
          <h2 className="display-medium mb-8" style={{ color: 'var(--text-primary)' }}>Quick Start Guide</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-brand-primary text-white rounded-sm flex items-center justify-center text-xl font-bold">
                  1
                </div>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Create Account</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Sign up with email or connect your Web3 wallet (MetaMask, Trust Wallet, WalletConnect)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-brand-primary text-white rounded-sm flex items-center justify-center text-xl font-bold">
                  2
                </div>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Complete KYC</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Verify your identity to unlock investment limits and access all features
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-brand-primary text-white rounded-sm flex items-center justify-center text-xl font-bold">
                  3
                </div>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Start Investing</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Browse projects, review details, and invest in token launches
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Wallet Setup */}
        <section className="mb-16">
          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center flex-shrink-0">
                  <Wallet className="text-brand-primary" size={24} />
                </div>
                <div>
                  <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>Wallet Setup</h2>
                  <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                    MatrixLaunch supports multiple Web3 wallets for seamless blockchain interaction.
                  </p>
                </div>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="metamask" className="bg-white/5 border border-white/25 rounded-sm px-6">
                  <AccordionTrigger style={{ color: 'var(--text-primary)' }}>
                    Setting Up MetaMask
                  </AccordionTrigger>
                  <AccordionContent style={{ color: 'var(--text-secondary)' }}>
                    <ol className="space-y-2 list-decimal list-inside">
                      <li>Install MetaMask extension from metamask.io</li>
                      <li>Create a new wallet or import existing seed phrase</li>
                      <li>Save your seed phrase securely (NEVER share it)</li>
                      <li>Add networks: Ethereum, BSC, Polygon via network settings</li>
                      <li>Click "Connect Wallet" on MatrixLaunch to link your wallet</li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="trust" className="bg-white/5 border border-white/25 rounded-sm px-6">
                  <AccordionTrigger style={{ color: 'var(--text-primary)' }}>
                    Using Trust Wallet
                  </AccordionTrigger>
                  <AccordionContent style={{ color: 'var(--text-secondary)' }}>
                    <ol className="space-y-2 list-decimal list-inside">
                      <li>Download Trust Wallet app from official app stores</li>
                      <li>Create new wallet and backup recovery phrase</li>
                      <li>Open DApp browser within Trust Wallet</li>
                      <li>Navigate to MatrixLaunch website</li>
                      <li>Wallet will auto-connect when you interact with platform</li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="walletconnect" className="bg-white/5 border border-white/25 rounded-sm px-6">
                  <AccordionTrigger style={{ color: 'var(--text-primary)' }}>
                    WalletConnect Protocol
                  </AccordionTrigger>
                  <AccordionContent style={{ color: 'var(--text-secondary)' }}>
                    <p className="mb-2">Use WalletConnect to connect any compatible mobile wallet:</p>
                    <ol className="space-y-2 list-decimal list-inside">
                      <li>Click "Connect Wallet" and select WalletConnect</li>
                      <li>Scan QR code with your mobile wallet app</li>
                      <li>Approve connection request in mobile wallet</li>
                      <li>You're now connected and ready to invest</li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </section>

        {/* Investment Process */}
        <section className="mb-16">
          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center flex-shrink-0">
                  <Zap className="text-brand-primary" size={24} />
                </div>
                <div>
                  <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>How to Invest in Projects</h2>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Step 1: Browse Projects</h3>
                  <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>Navigate to the Projects page to see:</p>
                  <ul className="space-y-1 ml-4" style={{ color: 'var(--text-secondary)' }}>
                    <li>• <strong>Live:</strong> Currently accepting investments</li>
                    <li>• <strong>Upcoming:</strong> Launches scheduled soon</li>
                    <li>• <strong>Completed:</strong> Successful past launches</li>
                  </ul>
                </div>

                <div>
                  <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Step 2: Review Project Details</h3>
                  <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>Click any project card to view:</p>
                  <ul className="space-y-1 ml-4" style={{ color: 'var(--text-secondary)' }}>
                    <li>• Token economics and distribution</li>
                    <li>• Team background and credentials</li>
                    <li>• Roadmap and milestones</li>
                    <li>• Smart contract audit reports</li>
                    <li>• Vesting schedules</li>
                  </ul>
                </div>

                <div>
                  <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Step 3: Make Investment</h3>
                  <ol className="space-y-2 list-decimal list-inside" style={{ color: 'var(--text-secondary)' }}>
                    <li>Click "Invest Now" on the project page</li>
                    <li>Enter investment amount (respecting min/max limits)</li>
                    <li>Review transaction details and fees</li>
                    <li>Approve token spending in your wallet</li>
                    <li>Confirm investment transaction</li>
                    <li>Wait for blockchain confirmation (1-5 minutes)</li>
                    <li>Receive investment confirmation email</li>
                  </ol>
                </div>

                <div>
                  <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Step 4: Track Your Investment</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Visit your Dashboard to monitor investment performance, claim tokens when vesting completes, and track ROI.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Referral Program */}
        <section className="mb-16">
          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center flex-shrink-0">
                  <Users className="text-brand-primary" size={24} />
                </div>
                <div>
                  <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>Referral Program Guide</h2>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Getting Your Referral Link</h3>
                  <ol className="space-y-2 list-decimal list-inside" style={{ color: 'var(--text-secondary)' }}>
                    <li>Log in to your account</li>
                    <li>Navigate to Dashboard → Referral section</li>
                    <li>Copy your unique referral code or link</li>
                    <li>Share via social media, email, or messaging apps</li>
                  </ol>
                </div>

                <div>
                  <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Earning Commissions</h3>
                  <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>You earn commissions when:</p>
                  <ul className="space-y-1 ml-4" style={{ color: 'var(--text-secondary)' }}>
                    <li>• Your direct referrals invest in projects (Level 1)</li>
                    <li>• Their referrals invest (Level 2-5)</li>
                    <li>• Commissions are paid instantly to your wallet</li>
                    <li>• No minimum threshold for withdrawals</li>
                  </ul>
                </div>

                <div>
                  <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Tracking Your Network</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Dashboard shows: total referrals, network size, earnings breakdown by level, pending commissions, and payment history.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Security Best Practices */}
        <section className="mb-16">
          <Card className="bg-white/5 border border-brand-primary backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center flex-shrink-0">
                  <Shield className="text-brand-primary" size={24} />
                </div>
                <div>
                  <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>Security Best Practices</h2>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="heading-2 mb-3 text-brand-primary">DO:</h3>
                  <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
                    <li>✓ Enable 2FA on your account</li>
                    <li>✓ Use hardware wallets for large amounts</li>
                    <li>✓ Verify URLs before connecting wallet</li>
                    <li>✓ Keep seed phrases offline and secure</li>
                    <li>✓ Update wallet software regularly</li>
                    <li>✓ Use strong, unique passwords</li>
                  </ul>
                </div>
                <div>
                  <h3 className="heading-2 mb-3 text-red-500">DON'T:</h3>
                  <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
                    <li>✗ Share your seed phrase with anyone</li>
                    <li>✗ Store private keys in cloud/email</li>
                    <li>✗ Click suspicious links in emails</li>
                    <li>✗ Connect wallet to unknown sites</li>
                    <li>✗ Screenshot private keys</li>
                    <li>✗ Use public WiFi for transactions</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* API Documentation */}
        <section>
          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center flex-shrink-0">
                  <Code className="text-brand-primary" size={24} />
                </div>
                <div>
                  <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>Developer API (Coming Soon)</h2>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    REST API for programmatic access to project data, investment statistics, and wallet integration. Documentation will be available Q2 2026.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default Documentation;
